import { ContentState } from "draft-js";
import { Api } from "@core/api/api";
import { organizationHeaders } from "@core/api/organizationHeaders";
import { pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { ProfileDto, profileFromDto } from "@domain/user/dtos";
import { Profile } from "@domain/user/profile";
import { PostCommentDto, postCommentFromDto, postCommentToDto } from "@domain/post/comment/dtos";
import { PostComment } from "@domain/post/comment/postComment";
import { PostCommentDraft } from "@domain/post/comment/postCommentDraft";
import { draftToDto, PostDto, postFromDto } from "./dtos";
import { Post, PostReactionType } from "./post";
import { PostDraft } from "./postDraft";
import { defaultPost, PostForOpenGraph, postFromDtoForOpenGraph } from "@domain/post/postOpenGraph/postOpenGraphDto";

const POST_URL = "/posts";
const POST_COMMENT_URL = "/posts/comments";

export class PostApi {
	constructor(private readonly api: Api) {}

	async create(draft: PostDraft, organizationId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(POST_URL, draftToDto(draft), organizationHeaders(organizationId));
		return postFromDto(result.data);
	}

	async update(id: string, draft: PostDraft, organizationId?: string): Promise<Post> {
		const result = await this.api.put<PostDto>(
			`${POST_URL}/${id}`,
			draftToDto(draft),
			organizationHeaders(organizationId)
		);
		return postFromDto(result.data);
	}

	delete(id: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${POST_URL}/${id}`, organizationHeaders(organizationId));
	}

	async get(id: string): Promise<Post> {
		const result = await this.api.get<PostDto>(`${POST_URL}/${id}`);
		return postFromDto(result.data);
	}
	async getPostForOpenGraph(id: string, isPostPage: boolean): Promise<PostForOpenGraph> {
		if (!isPostPage) return defaultPost();
		const result = await this.api.get<PostDto>(`${POST_URL}/${id}`);
		return postFromDtoForOpenGraph(result.data);
	}

	async getProfilePosts(id: string, pageOptions: PaginationOptions): Promise<Page<Post>> {
		const result = await this.api.get<Page<PostDto>>(`${POST_URL}?userId=${id}&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((post) => postFromDto(post)) };
	}

	async getOrganizationPosts(id: string, pageOptions: PaginationOptions, organizationId?: string): Promise<Page<Post>> {
		const result = await this.api.get<Page<PostDto>>(
			`${POST_URL}?organizationId=${id}&${pageParams(pageOptions)}`,
			organizationHeaders(organizationId)
		);
		return { ...result.data, content: result.data.content.map((post) => postFromDto(post)) };
	}

	/* async list(pageOptions: PaginationOptions, groupId?: string): Promise<Page<Post>> {
		const result = await this.api.get<Page<PostDto>>(
			`${POST_URL}?${pageParams(pageOptions)}${groupId ? `&groupId=${groupId}` : ""}`
		);
		return { ...result.data, content: result.data.content.map((post) => postFromDto(post)) };
	} */
	async list(pageOptions: PaginationOptions, groupId?: string): Promise<Page<Post>> {
		const result = await this.api.get<Page<PostDto>>(
			// `${POST_URL}?${pageParams(pageOptions)}${groupId ? `&groupId=${groupId}` : ""}&openFeed=true`
			`${POST_URL}?${pageParams(pageOptions)}${groupId ? `&groupId=${groupId}` : ""}`
		);
		return { ...result.data, content: result.data.content.map((post) => postFromDto(post)) };
	}

	async share(id: string, message?: ContentState, organizationId?: string, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(
			`${POST_URL}/${id}/share`,
			{
				text: message && contentStateToString(message),
				groupId,
			},
			organizationHeaders(organizationId)
		);
		return postFromDto(result.data);
	}

	async like(id: string, organizationId?: string): Promise<void> {
		return this.api.post(`${POST_URL}/${id}/likes`, undefined, organizationHeaders(organizationId));
	}
	async unLike(id: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${POST_URL}/${id}/likes`, organizationHeaders(organizationId));
	}

	getPostComment(id: string): Promise<PostComment> {
		throw Error(`Error fetching comment ${id}: method getComment is not implemented yet`);
	}

	async comment(postId: string, draft: PostCommentDraft, organizationId?: string): Promise<PostComment> {
		const result = await this.api.post<PostCommentDto>(
			`${POST_URL}/${postId}/comments`,
			postCommentToDto(draft),
			organizationHeaders(organizationId)
		);
		return postCommentFromDto(result.data);
	}

	async updateComment(commentId: string, draft: PostCommentDraft, organizationId?: string): Promise<PostComment> {
		const result = await this.api.put<PostCommentDto>(
			`${POST_COMMENT_URL}/${commentId}`,
			postCommentToDto(draft),
			organizationHeaders(organizationId)
		);
		return postCommentFromDto(result.data);
	}

	deleteComment(commentId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${POST_COMMENT_URL}/${commentId}`, organizationHeaders(organizationId));
	}

	async listComments(
		postId: string,
		pageOptions: PaginationOptions,
		organizationId?: string
	): Promise<Page<PostComment>> {
		const result = await this.api.get<Page<PostCommentDto>>(
			`${POST_URL}/${postId}/comments?${pageParams(pageOptions)}`,
			organizationHeaders(organizationId)
		);
		return { ...result.data, content: result.data.content.map((postComment) => postCommentFromDto(postComment)) };
	}

	async listReplies(
		commentId: string,
		pageOptions: PaginationOptions,
		organizationId?: string
	): Promise<Page<PostComment>> {
		const result = await this.api.get<Page<PostCommentDto>>(
			`${POST_COMMENT_URL}/${commentId}/children?${pageParams(pageOptions)}`,
			organizationHeaders(organizationId)
		);
		return { ...result.data, content: result.data.content.map((postComment) => postCommentFromDto(postComment)) };
	}

	async likeComment(commentId: string, organizationId?: string): Promise<void> {
		return this.api.post(`${POST_COMMENT_URL}/${commentId}/likes`, undefined, organizationHeaders(organizationId));
	}

	async unLikeComment(commentId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${POST_COMMENT_URL}/${commentId}/likes`, organizationHeaders(organizationId));
	}

	async getReactionProfiles(
		id: string,
		reaction: PostReactionType,
		pageOptions: PaginationOptions
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${POST_URL}/${id}/${reaction}/persons?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}

	async getReactionCommentsProfiles(
		commentId: string,
		reaction: PostReactionType,
		pageOptions: PaginationOptions
	): Promise<Page<Profile>> {
		const result = await this.api.get<Page<ProfileDto>>(
			`${POST_URL}/comments/${commentId}/${reaction}/persons?${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((profile) => profileFromDto(profile)) };
	}
}
